import { Link, NavLink as RRNavLink } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { FOOTER_NAV, GLOBAL_NAV, ROLES, SITE_NAV } from '@/app/nav'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { useObligations } from '@/lib/api/hooks'
import { initialsOf, useAuth } from '@/lib/auth/auth'
import { Logo } from './Logo'
import { cn } from '@/lib/utils'

function Item({
  to,
  icon: Icon,
  label,
  badge,
}: {
  to: string
  icon: LucideIcon
  label: string
  badge?: string
}) {
  return (
    <RRNavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/[0.14] font-semibold text-foreground [&_svg]:text-primary'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        )
      }
    >
      <Icon className="size-[17px] shrink-0" />
      <span className="truncate">{label}</span>
      {badge && (
        <span className="ml-auto rounded-full bg-ignition/[0.16] px-[7px] py-px text-[10.5px] font-bold text-ignition">
          {badge}
        </span>
      )}
    </RRNavLink>
  )
}

export function Sidebar() {
  const { role } = useRole()
  const { site, siteId } = useCurrentSite()
  const siteBase = siteId ? `/site/${siteId}` : '/home'
  // Real open-obligations count for the current site (badge on the Obligations item).
  const obligations = useObligations(siteId ?? '')
  const openObligations = (obligations.data ?? []).filter((o) => o.status === 'open').length
  const roleInfo = ROLES.find((r) => r.id === role)!
  const { user } = useAuth()
  const userName = user?.name || user?.email || 'Signed in'

  return (
    <aside className="flex h-full flex-col border-r bg-surface px-3 py-3.5">
      <div className="px-2 pb-4 pt-1.5">
        <Logo className="h-[15px] w-auto" />
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
        {GLOBAL_NAV.filter((i) => i.roles.includes(role)).map((i) => (
          <Item key={i.to} to={i.to} icon={i.icon} label={i.label} badge={i.badge} />
        ))}

        <div className="truncate px-2.5 pb-1.5 pt-4 font-display text-[9.5px] font-semibold uppercase tracking-[0.14em] text-faint">
          {site?.name ?? 'Site'}
        </div>
        {SITE_NAV.filter((i) => i.roles.includes(role)).map((i) => (
          <Item
            key={i.seg || 'overview'}
            to={`${siteBase}${i.seg ? `/${i.seg}` : ''}`}
            icon={i.icon}
            label={i.label}
            badge={i.seg === 'obligations'
              ? (siteId && openObligations > 0 ? String(openObligations) : undefined)
              : i.badge}
          />
        ))}

        <div className="mt-3 flex flex-col gap-0.5 border-t pt-3">
          {FOOTER_NAV.filter((i) => i.roles.includes(role)).map((i) => (
            <Item key={i.to} to={i.to} icon={i.icon} label={i.label} badge={i.badge} />
          ))}
        </div>
      </nav>

      <Link
        to="/account"
        className="mt-auto flex items-center gap-2.5 rounded-lg border-t px-1 pt-3 hover:bg-secondary"
      >
        <span className="grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-gradient-to-br from-stable to-contained font-display text-[12px] font-bold text-white">
          {initialsOf(userName)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold">{userName}</div>
          <div className="text-[11px] text-faint">{roleInfo.name}</div>
        </div>
      </Link>
    </aside>
  )
}
